/**
 * DocumentToolbar Component
 *
 * A comprehensive toolbar component designed specifically for document viewing interfaces.
 * Provides controls for page navigation, zoom, layout modes, and panel visibility.
 *
 * @author Ben Yee
 * @version 2.0.0
 */

import { ActionIcon, createStyles, Group, Tooltip, useMantineTheme, SegmentedControl, Center, Button } from '@mantine/core';
import {
    IconZoomIn,
    IconZoomOut,
    IconRotate, IconLayoutRows,
    IconLayoutSidebarLeftCollapse,
    IconLayoutSidebarLeftExpand,
    IconLayoutSidebarRightCollapse,
    IconLayoutSidebarRightExpand, IconRectangle, IconZoomOutArea
} from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { ApprovalLayoutTypes } from 'utils/constants';

const useStyles = createStyles((theme) => ({
    container: {
        position: 'relative',
        // top: 0,
        // zIndex: 100,
        borderBottom: theme.colorScheme === 'dark' ? `1px solid ${theme.colors.dark[4]}` : `1px solid ${theme.colors.gray[3]}`,
        // paddingTop: theme.spacing.xs,
        // paddingBottom: theme.spacing.xs,
        paddingTop: 6,
        paddingBottom: 6,
        paddingLeft: theme.spacing.md,
        paddingRight: theme.spacing.md,
        margin: 0,
        background: theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.white,
        boxShadow: theme.shadows.sm,

        // Ensure toolbar doesn't interfere with document content
        '&.document-toolbar': {
            userSelect: 'none',
            '-webkit-user-select': 'none',
            '-moz-user-select': 'none',
            '-ms-user-select': 'none',
        },
    },
}));

const DocumentToolbar = ({
    document,
    currentPageIndex = 0,
    totalPages = 0,
    onPageChange,
    zoom = 1,
    onZoomChange,
    rotation = 0,
    onRotationChange,
    layout = 'single',
    onLayoutChange,
    showThumbnails = true,
    onToggleThumbnails,
    showMetadata = true,
    onToggleMetadata,
    onFullscreen,
    onDownload,
    onPrint,
    showPageControls = true,
    showZoomControls = true,
    showLayoutControls = true,
    showPanelControls = true,
    settings,
}) => {
    const { classes } = useStyles();
    const theme = useMantineTheme();
    const [pageInputValue, setPageInputValue] = useState(currentPageIndex + 1);

    // Sync page input with current page
    useEffect(() => {
        setPageInputValue(currentPageIndex + 1);
    }, [currentPageIndex]);

    const handlePageInputChange = (value) => {
        setPageInputValue(value);
    };

    const handlePageInputKeyDown = (event) => {
        if (event.key === 'Enter') {
            const pageNumber = parseInt(pageInputValue, 10);
            if (pageNumber >= 1 && pageNumber <= totalPages) {
                onPageChange && onPageChange(pageNumber - 1);
            } else {
                // Reset to current page if invalid
                setPageInputValue(currentPageIndex + 1);
            }
        }
    };

    const handlePreviousPage = () => {
        if (currentPageIndex > 0) {
            onPageChange && onPageChange(currentPageIndex - 1);
        }
    };

    const handleNextPage = () => {
        if (currentPageIndex < totalPages - 1) {
            onPageChange && onPageChange(currentPageIndex + 1);
        }
    };

    const handleZoomIn = () => {
        const newZoom = Math.min(zoom + 0.25, 3);
        onZoomChange && onZoomChange(newZoom);
    };

    const handleZoomOut = () => {
        const newZoom = Math.max(zoom - 0.25, 0.25);
        onZoomChange && onZoomChange(newZoom);
    };

    const handleZoomAll = () => {
        // zoom the document to the optimal zoom
        onZoomChange && onZoomChange('extents');
    };

    const handleRotate = () => {
        const newRotation = (rotation + 90) % 360;
        onRotationChange && onRotationChange(newRotation);
    };

    const handleLayoutToggle = () => {
        const newLayout = layout === 'single' ? 'stacked' : 'single';
        onLayoutChange && onLayoutChange(newLayout);
    };

    const canGoToPrevious = currentPageIndex > 0;
    const canGoToNext = currentPageIndex < totalPages - 1;

    // const onLayoutChange = (val) => {
    //     const tmpSettings = { ...settings, layoutType: val };
    //     settingsChange(ApprovalEventTypes.SETTINGS_CHANGE, tmpSettings);
    // };
    return (
        <Group sx={{ width: '100%' }} position='apart' className={`document-toolbar ${classes.container} unhightable`} pr='md'>
            {/* Left Section - Panel Controls */}
            <Group sx={{ display: 'flex', width: '33%' }}>
                {showPanelControls && (
                    <>
                        <Tooltip position='bottom-start' label={showThumbnails ? 'Hide Thumbnails' : 'Show Thumbnails'}>
                            <ActionIcon variant='filled' onClick={onToggleThumbnails}>
                                {showThumbnails ? <IconLayoutSidebarLeftCollapse size={18} /> : <IconLayoutSidebarLeftExpand size={18} />}
                            </ActionIcon>
                        </Tooltip>

                        {/* {showLayoutControls && (
                            <Tooltip position='bottom' label={`Switch to ${layout === 'single' ? 'stacked' : 'single'} view`}>
                                <ActionIcon onClick={handleLayoutToggle} variant='subtle'>
                                    {layout === 'single' ? <IconLayoutRows size={18} /> : <IconLayoutGrid size={18} />}
                                </ActionIcon>
                            </Tooltip>
                        )} */}
                    </>
                )}

                <SegmentedControl
                    value={layout}
                    onChange={onLayoutChange}
                    data={[
                        {
                            value: ApprovalLayoutTypes.SINGLE,
                            label: (
                                <Center title='Single View'>
                                    <IconRectangle size={16} />
                                    {/* <Box ml={10}>Preview</Box> */}
                                </Center>
                            ),
                        },
                        {
                            value: ApprovalLayoutTypes.STACKED,
                            label: (
                                <Center title='Stacked View'>
                                    <IconLayoutRows size={16} />
                                    {/* <Box ml={10}>Code</Box> */}
                                </Center>
                            ),
                        },
                        // {
                        //     value: ApprovalLayoutTypes.COMPARE,
                        //     label: (
                        //         <Center title='Compare View'>
                        //             <IconSquareToggle size={16} />
                        //             {/* <Box ml={10}>Export</Box> */}
                        //         </Center>
                        //     ),
                        // },
                        // {
                        //     value: ApprovalLayoutTypes.COMMENT,
                        //     label: (
                        //         <Center title='Comment View'>
                        //             <IconMessage size={16} />
                        //             {/* <Box ml={10}>Export</Box> */}
                        //         </Center>
                        //     ),
                        // },
                    ]}
                />
            </Group>

            {/* Center Section - Page Navigation and Zoom Controls */}
            <Group sx={{ justifyContent: 'center' }}>
                {/* Page Navigation */}
                {/* {showPageControls && totalPages > 1 && (
                    <Group spacing='xs'>
                        <Tooltip label='Previous Page'>
                            <ActionIcon onClick={handlePreviousPage} disabled={!canGoToPrevious} variant='light'>
                                <IconArrowLeft size={16} />
                            </ActionIcon>
                        </Tooltip>

                        <Group spacing={4} align='center'>
                            <NumberInput
                                value={pageInputValue}
                                onChange={handlePageInputChange}
                                onKeyDown={handlePageInputKeyDown}
                                min={1}
                                max={totalPages}
                                size='xs'
                                style={{ width: 60 }}
                                hideControls
                            />
                            <Text size='sm' color='dimmed'>
                                of {totalPages}
                            </Text>
                        </Group>

                        <Tooltip label='Next Page'>
                            <ActionIcon onClick={handleNextPage} disabled={!canGoToNext} variant='light'>
                                <IconArrowRight size={16} />
                            </ActionIcon>
                        </Tooltip>
                    </Group>
                )} */}

                {/* Zoom Controls */}
                {showZoomControls && (
                    <Group spacing='xs' ml='md'>
                        

                        {/* <Text size='xs' sx={{ minWidth: 45, textAlign: 'center' }}>
                            {Math.round(zoom * 100)}%
                        </Text> */}
<Button.Group >
                        <Tooltip label='Zoom In'>
                            <Button size='xs' variant="default" onClick={handleZoomIn} disabled={zoom >= 3} >
                                <IconZoomIn size={16} />
                            </Button>
                        </Tooltip>
                        <Tooltip label='Zoom Out'>
                            <Button size='xs' variant="default" onClick={handleZoomOut} disabled={zoom <= 0.25} >
                                <IconZoomOut size={16} />
                            </Button>
                        </Tooltip>
                        <Tooltip label='Zoom All'>
                            <Button size='xs' variant="default" onClick={handleZoomAll} disabled={zoom >= 3} >
                                <IconZoomOutArea size={16} />
                            </Button>
                        </Tooltip>

                        {layout === 'single' && (
                            <Tooltip label='Rotate'>
                                <Button size='xs' variant="default" onClick={handleRotate} >
                                    <IconRotate size={16} />
                                </Button>
                            </Tooltip>
                        )}
                        </Button.Group>
                    </Group>
                )}
            </Group>

            {/* Right Section - Document Actions and Panel Controls */}
            <Group sx={{ display: 'flex', justifyContent: 'end', width: '33%' }}>
                {/* Document Actions */}
                <Button.Group spacing='xs'>
                    {/* {onDownload && (
                        <Tooltip label='Download Document'>
                            <Button size='xs' variant="default" onClick={onDownload} >
                                <IconDownload size={16} />
                            </Button>
                            <FileDownloader onComplete={() => onFileDownload()} url={document.url} filename={document.filename} />
                        </Tooltip>
                    )}

                    {onPrint && (
                        <Tooltip label='Print Document'>
                            <Button size='xs' variant="default" onClick={onPrint} >
                                <IconPrinter size={16} />
                            </Button>
                        </Tooltip>
                    )} */}

                    {/* {onFullscreen && (
                        <Tooltip label='Fullscreen'>
                            <ActionIcon onClick={onFullscreen} variant='light'>
                                <IconArrowsMaximize size={16} />
                            </ActionIcon>
                        </Tooltip>
                    )} */}
                </Button.Group>

                {/* Metadata Panel Toggle */}
                {showPanelControls && (
                    <Tooltip position='bottom' label={showMetadata ? 'Hide Metadata' : 'Show Metadata'}>
                        <ActionIcon variant='filled' onClick={onToggleMetadata}>
                            {showMetadata ? <IconLayoutSidebarRightCollapse size={18} /> : <IconLayoutSidebarRightExpand size={18} />}
                        </ActionIcon>
                    </Tooltip>
                )}
            </Group>
        </Group>
    );
};

export default DocumentToolbar;
